import { lazy } from 'react';
import { SimpleGrid, Box, Text, Group, useMantineTheme, useComputedColorScheme, ThemeIcon, Progress, RingProgress } from '@mantine/core';
import {
  IconChecklist,
  IconPlayerPlay,
  IconEye,
  IconCheck,
  IconAlertCircle,
  IconClock,
} from '@tabler/icons-react';
import { WidgetProps, TaskStats as TaskStatsType } from '../types';
import { registerWidget } from '../registry';
import { FadeIn } from '../../../animations';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

// ============================================================================
// TaskStats Widget Component
// ============================================================================

function TaskStatsWidget({ globalData, isCollapsed, instanceId, settings }: WidgetProps) {
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme('light');
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  // Use computed text color if available (from container settings)
  const textColor = settings?.computedTextColor;
  const dimmedColor = textColor || 'dimmed';

  if (isCollapsed) return null;

  const stats = globalData?.taskStats || {
    total: 0,
    todo: 0,
    inProgress: 0,
    review: 0,
    done: 0,
    blocked: 0,
    overdue: 0,
  };

  const completionRate = stats.total > 0 ? Math.round((stats.done / stats.total) * 100) : 0;

  const statItems = [
    {
      label: 'To Do',
      value: stats.todo,
      icon: IconChecklist,
      color: 'gray',
    },
    {
      label: 'In Progress',
      value: stats.inProgress,
      icon: IconPlayerPlay,
      color: 'blue',
    },
    {
      label: 'Review',
      value: stats.review,
      icon: IconEye,
      color: 'yellow',
    },
    {
      label: 'Done',
      value: stats.done,
      icon: IconCheck,
      color: 'green',
    },
    {
      label: 'Blocked',
      value: stats.blocked,
      icon: IconAlertCircle,
      color: 'red',
    },
    {
      label: 'Overdue',
      value: stats.overdue,
      icon: IconClock,
      color: 'orange',
    },
  ];

  return (
    <FadeIn style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
        {/* Completion Ring */}
        <Group justify="center" mb="sm">
          <RingProgress
            size={80}
            thickness={8}
            roundCaps
            sections={[{ value: completionRate, color: 'green' }]}
            label={
              <Box ta="center">
                <Text size="md" fw={700} c={textColor} style={accessibilityStyles}>
                  {completionRate}%
                </Text>
                <Text size="xs" c={dimmedColor} style={accessibilityStyles}>
                  Complete
                </Text>
              </Box>
            }
          />
        </Group>

        {/* Stats Grid */}
        <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="xs">
          {statItems.map((item) => {
            const Icon = item.icon;
            return (
              <Box
                key={item.label}
                p="xs"
                style={{
                  backgroundColor: colorScheme === 'dark'
                    ? theme.colors[item.color][9]
                    : theme.colors[item.color][0],
                  borderRadius: theme.radius.sm,
                  textAlign: 'center',
                }}
              >
                <Group gap={4} justify="center" mb={2}>
                  <ThemeIcon size="xs" variant="transparent" color={item.color}>
                    <Icon size={12} />
                  </ThemeIcon>
                  <Text size="xs" c={dimmedColor} style={accessibilityStyles}>
                    {item.label}
                  </Text>
                </Group>
                <Text size="md" fw={700} c={textColor || item.color} style={accessibilityStyles}>
                  {item.value}
                </Text>
              </Box>
            );
          })}
        </SimpleGrid>

        {/* Total */}
        <Box
          mt="sm"
          p="xs"
          style={{
            backgroundColor: colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
            borderRadius: theme.radius.sm,
            textAlign: 'center',
          }}
        >
          <Text size="xs" c={dimmedColor} style={accessibilityStyles}>
            Total Tasks
          </Text>
          <Text size="lg" fw={700} c={textColor} style={accessibilityStyles}>
            {stats.total}
          </Text>
        </Box>
      </Box>
    </FadeIn>
  );
}

// ============================================================================
// Widget Registration
// ============================================================================

registerWidget({
  id: 'task-stats',
  name: 'Task Statistics',
  description: 'Overview of task metrics with completion rate and status breakdown',
  icon: IconChecklist,
  category: 'stats',
  component: lazy(() => Promise.resolve({ default: TaskStatsWidget })),
  sizes: {
    base: { cols: 12, rows: 5 },
    sm: { cols: 6, rows: 5 },
    md: { cols: 6, rows: 5 },
    lg: { cols: 4, rows: 5 },
    xl: { cols: 4, rows: 5 },
    tv: { cols: 3, rows: 5 },
  },
  sizeVariants: {
    variants: ['compact', 'standard', 'wide'],
    defaultVariant: 'standard',
    variantSizes: {
      compact: {
        base: { cols: 12, rows: 4 },
        sm: { cols: 6, rows: 4 },
        md: { cols: 4, rows: 4 },
        lg: { cols: 3, rows: 4 },
        xl: { cols: 3, rows: 4 },
        tv: { cols: 2, rows: 4 },
      },
      standard: {
        base: { cols: 12, rows: 5 },
        sm: { cols: 6, rows: 5 },
        md: { cols: 6, rows: 5 },
        lg: { cols: 4, rows: 5 },
        xl: { cols: 4, rows: 5 },
        tv: { cols: 3, rows: 5 },
      },
      wide: {
        base: { cols: 12, rows: 4 },
        sm: { cols: 12, rows: 4 },
        md: { cols: 8, rows: 4 },
        lg: { cols: 6, rows: 4 },
        xl: { cols: 6, rows: 4 },
        tv: { cols: 4, rows: 4 },
      },
      full: {
        base: { cols: 12, rows: 5 },
        sm: { cols: 12, rows: 5 },
        md: { cols: 12, rows: 5 },
        lg: { cols: 12, rows: 5 },
        xl: { cols: 12, rows: 5 },
        tv: { cols: 12, rows: 5 },
      },
    },
  },
  data: {
    sourceType: 'global',
    globalDataKeys: ['taskStats'],
  },
  hasSettings: true,
  expandable: true,
});

export default TaskStatsWidget;
